<script setup lang="ts">
import CircularLoader from '../Loader/CircularLoader.vue';
import { type Component, computed, ref, watch, watchEffect } from 'vue';
import { usePage } from '@inertiajs/vue3';
import {
	CheckCircleIcon,
	ExclamationTriangleIcon,
	InformationCircleIcon,
	XMarkIcon,
} from '@heroicons/vue/24/outline';

export type NotificationMessageStatus =
	| 'success'
	| 'warning'
	| 'error'
	| 'danger'
	| 'info';

type NotificationMessagePosition =
	| 'top-left'
	| 'top-center'
	| 'top-right'
	| 'bottom-left'
	| 'bottom-center'
	| 'bottom-right';

export interface NotificationMessageProps {
	type: 'flotable' | 'base' | 'abs';
	status: NotificationMessageStatus;
	position?: NotificationMessagePosition;
	timeout?: number;
	title?: string;
	message?: string;
	flash?: {
		bannerStyle: NotificationMessageStatus;
		banner: string;
		skipNotify?: boolean;
	};
	shiftLeft?: boolean;
}

const show = defineModel<boolean>();
const props = withDefaults(defineProps<NotificationMessageProps>(), {
	number: false,
	message: undefined,
	flash: undefined,
	position: 'top-center',
	shiftLeft: false,
});
const page = usePage();
const timeoutRef = ref();
const flashMessage = ref<string | undefined>();
const notificationMessage = computed(() => flashMessage.value ?? props.message);
const notificationStatus = ref<NotificationMessageStatus>(props.status);
const stopAnimation = ref(false);
const colorMap: Record<string, string> = {
	success: '#00a96e',
	warning: '#f4a827',
	error: '#fe196d',
	danger: '#fe196d',
	info: '#6725f1',
};

const iconMap: Record<string, Component> = {
	success: CheckCircleIcon,
	warning: ExclamationTriangleIcon,
	error: XMarkIcon,
	danger: XMarkIcon,
	info: InformationCircleIcon,
};

const styleProps = computed(() => {
	const { status, type } = props;
	const isBase = type === 'base';

	return {
		foreground: colorMap[notificationStatus.value],
		icon: iconMap[notificationStatus.value],
		iconBgClasses: [
			{ 'bg-white': isBase && !['error', 'danger', 'info'].includes(status) },
			{ 'bg-error/10': isBase && ['error', 'danger'].includes(status) },
			{ 'bg-gray-100': isBase && status === 'info' },
			{ 'rounded-xl p-2': isBase },
		],
	};
});

const animationName = computed(() => {
	if (props.type === 'flotable') {
		if (props.position === 'top-center') return 'slide_top_center';
		else if (props.position === 'bottom-center') return 'slide_bottom_center';
		else if (props.position.includes('left')) return 'slide_left';
		else if (props.position.includes('right')) return 'slide_right';
	}
	return '';
});

const stop = () => {
	if (props.timeout) {
		stopAnimation.value = true;
		clearTimeout(timeoutRef.value);
	}
};

const close = () => {
	show.value = false;
	flashMessage.value = undefined;
	if (props.timeout) {
		clearTimeout(timeoutRef.value);
		stopAnimation.value = false;
	}
};

watchEffect(async () => {
	const { flash } = page.props?.jetstream ?? {};

	if (props.flash && flash && !Array.isArray(flash) && !flash?.skipNotify) {
		show.value = true;
		notificationStatus.value = flash.bannerStyle ?? 'info';
		flashMessage.value = flash.banner;
		timeoutRef.value = setTimeout(() => close(), props.timeout);
	}
});

watch(show, () => {
	if (show.value && props.timeout) {
		timeoutRef.value = setTimeout(() => close(), props.timeout);
	}
});
</script>

<template>
	<Transition :name="animationName" mode="in-out">
		<div
			v-if="show"
			class="notification"
			:class="[
				type,
				notificationStatus,
				{
					[position as NotificationMessagePosition]:
						type === 'flotable' || type === 'abs',
				},
				{ '-translate-x-4': shiftLeft },
			]"
			@mouseover="stop"
		>
			<span class="flex items-center">
				<slot v-if="$slots.start" name="start" />
				<span v-else-if="styleProps.icon" :class="styleProps.iconBgClasses">
					<component :is="styleProps.icon" class="size-8" />
				</span>
			</span>
			<div class="flex grow items-center overflow-auto pr-[20%]">
				<slot v-if="$slots.content" name="content" />
				<div v-else>
					<h4 v-if="title" class="text-left text-lg font-medium">
						{{ title }}
					</h4>
					<p
						v-if="notificationMessage"
						class="text-left text-base font-normal leading-6 text-black"
					>
						{{ notificationMessage }}
					</p>
				</div>
			</div>
			<span class="flex items-center">
				<slot v-if="$slots.end" name="end" />
				<span v-if="timeout">
					<CircularLoader
						:duration="timeout"
						:foreground="styleProps.foreground"
						class="size-10"
						:stop-animation="stopAnimation"
					>
						<button class="border-none" @click="close">
							<XMarkIcon class="size-6 text-base-300" />
						</button>
					</CircularLoader>
				</span>
			</span>
		</div>
	</Transition>
</template>

<style lang="css" scoped>
.notification {
	@apply flex justify-between gap-4 rounded-lg p-4;
}

.abs {
	@apply bordered absolute z-50 w-[85vw] max-w-[600px] border border-l-4 bg-white shadow-xl;
}

.flotable {
	@apply bordered fixed z-[60] w-[85vw] max-w-[600px] border border-l-4 bg-white shadow-xl;
}

.flotable.success,
.abs.success {
	@apply border-success text-success;
}

.flotable.warning,
.abs.warning {
	@apply border-warning text-warning;
}
.flotable.error,
.flotable.danger,
.abs.error,
.abs.danger {
	@apply border-error text-error;
}

.flotable.info,
.abs.info {
	@apply border-primary text-primary;
}

.flotable.top-left,
.abs.top-left {
	@apply left-8 top-8;
}
.flotable.top-center,
.abs.top-center {
	@apply left-1/2 top-8 -translate-x-1/2;
}
.flotable.top-right,
.abs.top-right {
	@apply right-8 top-8;
}

.flotable.bottom-left,
.abs.bottom-left {
	@apply bottom-8 left-8;
}
.flotable.bottom-center,
.abs.bottom-center {
	@apply bottom-8 left-1/2 -translate-x-1/2;
}
.flotable.bottom-right,
.abs.bottom-right {
	@apply bottom-8 right-8;
}

.base.success {
	@apply bg-success/5 text-success;
}

.base.warning {
	@apply bg-warning/10 text-warning;
}
.base.error,
.base.danger {
	@apply bg-error/5 text-error;
}
.base.info {
	@apply border border-base-200 bg-white text-primary;
}
</style>
